// End-to-end DMN simulator.
//
//  const sim = new DmnSimulator({ model });
//  const { results, trace, context } = sim.evaluate({ Customer: 'Business', OrderSize: 12 });
//
// The simulator walks all DRG elements in dependency order, binds:
//   • InputData → the caller-supplied value (by variable.name ‖ id ‖ name)
//   • BKM       → a closure (from FunctionDefinition body) bound by variable.name
//   • Decision  → the evaluated decisionLogic result bound by variable.name
//
// All expressions share one FEEL env so boxed expressions can reference
// upstream values.

import { topoSort } from './topoSort';
import { evalExpression, evalFunctionDefinition } from './evalExpression';
import { evalDecisionTable } from './evalDecisionTable';
import { sanitizeFeelIdent } from '../feel/ident';

export class DmnSimulatorError extends Error {
  constructor(msg) { super(`[simulator] ${msg}`); }
}

export class DmnSimulator {
  constructor({ model }) {
    if (!model) throw new DmnSimulatorError('model is required');
    this._model = model;
  }

  /**
   * Evaluate all decisions in the DRG.
   * @param {Record<string, any>} inputs  caller-supplied input-data values
   * @returns {{ context: object, results: object, trace: Array, warnings: Array }}
   */
  evaluate(inputs = {}) {
    const trace = [];
    const warnings = [];
    const context = { ...inputs };
    const results = {};

    const order = topoSort(this._model.drgElements);
    const byId = new Map(this._model.drgElements.map((el) => [el.id, el]));

    order.forEach((id) => {
      const el = byId.get(id);
      if (!el) return;
      try {
        if (el.kind === 'inputData') {
          this._bindInputData(el, inputs, context, trace);
        } else if (el.kind === 'businessKnowledgeModel') {
          this._bindBkm(el, context, trace);
        } else if (el.kind === 'decision') {
          this._evaluateDecision(el, context, results, trace);
        }
      } catch (err) {
        const errContext = err && err.context ? err.context : null;
        const logicType = (el.raw && el.raw.decisionLogic && el.raw.decisionLogic.$type) || null;
        trace.push({ id, kind: el.kind, error: err.message, errorContext: errContext, logicType });
        warnings.push({ id, message: err.message, context: errContext });
      }
    });

    return { context, results, trace, warnings };
  }

  /**
   * Evaluate a single decision, pulling in its upstream dependencies.
   */
  evaluateDecision(decisionId, inputs = {}) {
    const full = this.evaluate(inputs);
    if (!(decisionId in full.results)) {
      throw new DmnSimulatorError(`decision '${decisionId}' not found or failed to evaluate`);
    }
    return { value: full.results[decisionId], context: full.context, trace: full.trace, warnings: full.warnings };
  }

  _bindInputData(el, inputs, context, trace) {
    const raw = el.raw;
    const variable = raw.variable || {};
    // Bind under raw display name (so caller-supplied inputs like
    // `{ "Credit Score": 720 }` keep working) AND under the sanitized FEEL
    // ident (so an in-expression reference like `CreditScore` resolves).
    const names = uniq([variable.name, el.name, sanitizeFeelIdent(el.name), el.id]);
    // pick first-available key in inputs, else undefined
    let value;
    let usedKey = null;
    for (const n of names) {
      if (n && Object.prototype.hasOwnProperty.call(inputs, n)) {
        value = inputs[n];
        usedKey = n;
        break;
      }
    }
    names.forEach((n) => { if (n) context[n] = value; });
    trace.push({ id: el.id, kind: 'inputData', bound: names, key: usedKey, value });
  }

  _bindBkm(el, context, trace) {
    const raw = el.raw;
    const logic = raw.encapsulatedLogic;
    const variable = raw.variable || {};
    // Bind under raw display name (so caller-supplied inputs like
    // `{ "Credit Score": 720 }` keep working) AND under the sanitized FEEL
    // ident (so an in-expression reference like `CreditScore` resolves).
    const names = uniq([variable.name, el.name, sanitizeFeelIdent(el.name), el.id]);
    let fn;
    if (logic && logic.$type === 'dmn:FunctionDefinition') {
      fn = evalFunctionDefinition(logic, context);
    } else if (logic) {
      // non-FunctionDefinition encapsulatedLogic — treat as a zero-arg expression
      const value = evalExpression(logic, context);
      fn = () => value;
      fn.__feelParams = [];
    } else {
      fn = () => null;
      fn.__feelParams = [];
    }
    names.forEach((n) => { if (n) context[n] = fn; });
    trace.push({ id: el.id, kind: 'businessKnowledgeModel', bound: names });
  }

  _evaluateDecision(el, context, results, trace) {
    const raw = el.raw;
    const logic = raw.decisionLogic;
    const variable = raw.variable || {};
    // Bind under raw display name (so caller-supplied inputs like
    // `{ "Credit Score": 720 }` keep working) AND under the sanitized FEEL
    // ident (so an in-expression reference like `CreditScore` resolves).
    const names = uniq([variable.name, el.name, sanitizeFeelIdent(el.name), el.id]);
    if (!logic) {
      results[el.id] = null;
      trace.push({ id: el.id, kind: 'decision', value: null, skipped: 'no decisionLogic' });
      return;
    }
    let value;
    const extra = {};
    if (logic.$type === 'dmn:DecisionTable') {
      const info = evalDecisionTable(logic, context);
      value = info.result;
      extra.tableId = logic.id || null;
      extra.matchedRules = info.matchedRules;
      extra.inputValues = info.inputValues;
      extra.hitPolicy = info.hit;
    } else {
      value = evalExpression(logic, context);
    }
    // ML decisions resolve asynchronously. The simulator's public surface is
    // synchronous today, so we surface the Promise — callers that want the
    // resolved value can use evaluateAsync() (added below) or await on the
    // returned results entries themselves.
    results[el.id] = value;
    names.forEach((n) => { if (n) context[n] = value; });
    trace.push({ id: el.id, kind: 'decision', logicType: logic.$type, value, ...extra });
  }

  /**
   * Async variant — same semantics as `evaluate()` but awaits any Promise
   * that decision logic returns (i.e. epx:MachineLearningDecision). Use this
   * when the model contains ML decisions and you need fully resolved values
   * downstream.
   */
  async evaluateAsync(inputs = {}) {
    const out = this.evaluate(inputs);
    const ids = Object.keys(out.results);
    for (const id of ids) {
      const v = out.results[id];
      if (v && typeof v.then === 'function') {
        try {
          const resolved = await v;
          out.results[id] = resolved;
          // Also rebind the resolved value into context under any names that
          // currently hold the Promise so downstream FEEL refs see a value.
          Object.keys(out.context).forEach((k) => {
            if (out.context[k] === v) out.context[k] = resolved;
          });
          out.trace.forEach((t) => { if (t.id === id && t.value === v) t.value = resolved; });
        } catch (err) {
          out.results[id] = null;
          out.warnings.push({ id, message: err.message });
          out.trace.forEach((t) => { if (t.id === id) { t.error = err.message; t.value = null; }});
        }
      }
    }
    return out;
  }
}

function uniq(arr) {
  const seen = new Set();
  const out = [];
  arr.forEach((v) => {
    if (!v || seen.has(v)) return;
    seen.add(v);
    out.push(v);
  });
  return out;
}
